/**
 * PATH: packages/ui/src/kanban/card-surface/telemetry.ts
 * FILE_ID: UI-KANBAN-CARDSURFACE-TELEMETRY-001
 * PURPOSE: Publish privacy-safe runtime seam events for card-surface rollout probes.
 * WHY: Canary decisions require evidence without exposing card titles, ids or content.
 * FLOW: runtime/command event → bounded __QT_OBS__ ring + CustomEvent listener.
 * DEPENDENCIES: browser globals only.
 * RELATED: runtime/useCardSurfaceRuntime.ts; rollout.ts; P5 CI/probe gates.
 */
import type {
  CardCommandId,
  CardSurfaceId,
  CardSurfaceRuntime,
} from './contract'
import type { CardSurfaceRolloutMode } from './rollout'

export type CardSurfaceTelemetryEvent =
  | 'runtime_ready'
  | 'surface_ready'
  | 'command_invoked'
  | 'command_failed'

export interface CardSurfaceTelemetryPayload {
  event: CardSurfaceTelemetryEvent
  /** Rollout selection is independent from runtime authorization mode. */
  rolloutMode: CardSurfaceRolloutMode
  runtimeMode: CardSurfaceRuntime['mode']
  surfaceId: CardSurfaceId
  boardScoped: boolean
  capabilityCount: number
  command?: CardCommandId
  at: number
}

type QtObsSlot = {
  kanbanCardSurface?: CardSurfaceTelemetryPayload[]
  [key: string]: unknown
}

const MAX_EVENTS = 100

function isObservationSlot(value: unknown): value is QtObsSlot {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function appendToObservation(event: CardSurfaceTelemetryPayload): void {
  try {
    const target = window as unknown as { __QT_OBS__?: unknown }
    const existing = target.__QT_OBS__
    const obs: QtObsSlot = isObservationSlot(existing) ? existing : {}
    if (obs !== existing) {
      target.__QT_OBS__ = obs
    }
    // A frozen or proxy-backed observation slot must never block the user
    // command that happened to emit telemetry. Skip it rather than mutating it.
    if (!Object.isExtensible(obs)) return

    const ring = Array.isArray(obs.kanbanCardSurface)
      ? obs.kanbanCardSurface
      : []
    if (ring !== obs.kanbanCardSurface) {
      obs.kanbanCardSurface = ring
    }
    ring.push(event)
    if (ring.length > MAX_EVENTS) ring.splice(0, ring.length - MAX_EVENTS)
  } catch {
    // Telemetry is fail-soft. In particular, read-only browser globals and
    // malformed instrumentation must not replace an action's real error.
  }
}

function dispatchObservationEvent(event: CardSurfaceTelemetryPayload): void {
  try {
    window.dispatchEvent(
      new CustomEvent('quotech:kanban-card-surface', { detail: event }),
    )
  } catch {
    // Browser event hooks are optional observational plumbing.
  }
}

export function emitCardSurfaceTelemetry(
  payload: Omit<CardSurfaceTelemetryPayload, 'at'>,
): void {
  if (typeof window === 'undefined') return
  const event: CardSurfaceTelemetryPayload = { ...payload, at: Date.now() }
  appendToObservation(event)
  dispatchObservationEvent(event)
}
